// src/components/GlobalPlayerUI.js
import React, { useContext, useState, useRef, useEffect } from 'react';
import { AudioContext } from '../contexts/AudioContext';
import './GlobalPlayerUI.css';

const SLEEP_OPTIONS = [
  { label: '10s', value: 10 },
  { label: '5 mins', value: 5 * 60 },
  { label: '10 mins', value: 10 * 60 },
  { label: '15 mins', value: 15 * 60 },
  { label: '30 mins', value: 30 * 60 },
];

const GlobalPlayerUI = () => {
  const {
    currentEpisode,
    isPlaying,
    duration,
    currentTime,
    volume,
    isLoadingMetadata,
    togglePlayPause,
    seek,
    setVolume,
    bufferedTime,
  } = useContext(AudioContext);

  const [sleepActive, setSleepActive] = useState(false);
  const [showSleepMenu, setShowSleepMenu] = useState(false);
  const [sleepValue, setSleepValue] = useState(null);
  const sleepBtnRef = useRef(null);
  const sleepMenuRef = useRef(null);
  const timerRef = useRef(null);
  
  const volumeSliderRef = useRef(null);

  useEffect(() => {
    if (volumeSliderRef.current) {
      const volumePercentage = volume * 100;
      volumeSliderRef.current.style.setProperty('--volume-progress', `${volumePercentage}%`);
    }
  }, [volume]);


  // Close menu on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        sleepMenuRef.current &&
        !sleepMenuRef.current.contains(event.target) &&
        sleepBtnRef.current &&
        !sleepBtnRef.current.contains(event.target)
      ) {
        setShowSleepMenu(false);
      }
    }
    if (showSleepMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSleepMenu]);

  // When sleepValue is set, set sleepActive true
  useEffect(() => {
    setSleepActive(!!sleepValue);
  }, [sleepValue]);

  // Auto-pause logic for sleep timer
  useEffect(() => {
    if (!sleepValue) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      if (isPlaying) togglePlayPause();
      setSleepValue(null);
    }, sleepValue * 1000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sleepValue, isPlaying, togglePlayPause]);

  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds) || timeInSeconds === Infinity || timeInSeconds < 0) {
      return '00:00';
    }
    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor((timeInSeconds % 3600) / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    } else {
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
  };

  if (!currentEpisode && !isLoadingMetadata) {
    return null; 
  }

  if (isLoadingMetadata && !currentEpisode) { 
      return (
        <div className="global-player">
            <div>Loading audio...</div>
        </div>
      );
  }

  return (
    <div className="global-player">
      <div className="progress-bar-top-container">
        <span className="time-display">{formatTime(currentTime)}</span>
        <div className="progress-bar-base" />
        <div className="progress-bar-buffered" style={{width: duration > 0 ? `${(bufferedTime/duration)*100}%` : '0%'}} />
        <input
          type="range"
          min="0"
          max={duration > 0 ? duration : 100}
          value={currentTime}
          onChange={(e) => seek(parseFloat(e.target.value))}
          title="Seek"
          disabled={!currentEpisode || duration === 0}
          className="progress-bar-black"
          style={{
            background: duration > 0
              ? `linear-gradient(to right, #000 0%, #000 ${(currentTime/duration)*100}%, transparent ${(currentTime/duration)*100}%, transparent 100%)`
              : 'transparent'
          }}
        />
        <span className="time-display">{formatTime(duration - currentTime)}</span>
      </div>
      <div className="global-player-info">
        <img 
          src={currentEpisode?.image || 'https://via.placeholder.com/40?text=...'} 
          alt={currentEpisode?.title || "No episode selected"} 
        />
        <span className="episode-title" title={currentEpisode?.title}>
          {currentEpisode?.title || "No Episode Selected"}
        </span>
      </div>
      <div className="global-player-controls">
        <button onClick={() => seek(Math.max(currentTime - 10, 0))} title="Back 10 seconds">
          <img src={require('../assets/arrow-ccw.svg').default} alt="Back 10 seconds" style={{width: 24, height: 24}} />
        </button>
        <button onClick={togglePlayPause} title={isPlaying ? "Pause" : "Play"}>
          <img 
            src={isPlaying ? require('../assets/pause-icon.svg').default : require('../assets/play-icon.svg').default}
            alt={isPlaying ? 'Pause' : 'Play'}
            style={{width: 32, height: 32}}
          />
        </button>
        <button onClick={() => seek(Math.min(currentTime + 30, duration))} title="Forward 30 seconds">
          <img src={require('../assets/arrow-cw.svg').default} alt="Forward 30 seconds" style={{width: 24, height: 24}} />
        </button>
        <button
          ref={sleepBtnRef}
          onClick={() => setShowSleepMenu((prev) => !prev)}
          title="Sleep Timer"
          style={{ position: 'relative' }}
        >
          <img 
            src={sleepActive ? require('../assets/sleep-active.svg').default : require('../assets/sleep-default.svg').default}
            alt="Sleep Timer"
            style={{width: 24, height: 24}}
          />
          {showSleepMenu && (
            <div
              ref={sleepMenuRef}
              className="sleep-timer-menu"
            >
              {sleepValue && (
                <>
                  <div
                    className="sleep-timer-menu__item sleep-timer-menu__item--clear"
                    onClick={() => { setSleepValue(null); setShowSleepMenu(false); }}
                  >
                    Clear timer
                  </div>
                  <div className="sleep-timer-menu__divider" />
                </>
              )}
              {SLEEP_OPTIONS.map(opt => (
                <div
                  key={opt.value}
                  onClick={() => { setSleepValue(opt.value); setShowSleepMenu(false); }}
                  className={`sleep-timer-menu__item${sleepValue === opt.value ? ' selected' : ''}`}
                >
                  {opt.label}
                </div>
              ))}
            </div>
          )}
        </button>
      </div>

      <div className="volume-controls">
        <img src={require('../assets/speaker.svg').default} alt="Speaker" className="icon-placeholder" style={{width: 24, height: 24}} />
        <input
          ref={volumeSliderRef}
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={volume}
          onChange={(e) => setVolume(parseFloat(e.target.value))}
          title="Volume"
          disabled={!currentEpisode}
        />
      </div>
    </div>
  );
};

export default GlobalPlayerUI;
